import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { AuthLoadingScreen } from '@client/components/auth/AuthLoadingScreen';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card } from 'antd';
import { AlertCircle, CheckCircle, Home, LogIn } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

// OAuth admin consent callback parameters
const searchSchema = z.object({
    admin_consent: z.enum(['True', 'False']).optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
    // State can be either a string (URL-encoded JSON) or an object (already parsed)
    state: z
        .union([
            z.string(),
            z.object({
                orgId: z.string().optional(),
                tenantName: z.string().optional(),
                redirectTo: z.string().optional(),
            }),
        ])
        .optional(),
});

// Map OAuth error codes to user-friendly messages
const errorCodeMessages: Record<string, { title: string; description: string }> = {
    access_denied: {
        title: 'Consent Declined',
        description:
            'You have declined to grant admin consent. Your organization users will not be able to use single sign-on.',
    },
    unauthorized_client: {
        title: 'Unauthorized Application',
        description: 'This application is not authorized to request admin consent for your organization.',
    },
    invalid_request: {
        title: 'Invalid Request',
        description: 'The consent request was invalid. Please try again or contact support.',
    },
    server_error: {
        title: 'Server Error',
        description: 'The identity provider encountered an error. Please try again later.',
    },
    temporarily_unavailable: {
        title: 'Service Temporarily Unavailable',
        description: 'The consent service is temporarily unavailable. Please try again in a few minutes.',
    },
};

export const Route = createFileRoute('/admin-consent')({
    validateSearch: zodValidator(searchSchema),
    component: AdminConsent,
});

function AdminConsent() {
    const { admin_consent, error: errorCode, error_description, state } = Route.useSearch();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(true);

    // Parse state parameter if present
    const stateData = useMemo(() => {
        if (!state) return {};

        // If state is already an object (auto-parsed by router)
        if (typeof state === 'object') {
            return {
                orgId: state.orgId ? BigInt(state.orgId) : undefined,
                tenantName: state.tenantName,
                redirectTo: state.redirectTo,
            };
        }

        // If state is a string, try to parse it as JSON
        try {
            const parsed = JSON.parse(decodeURIComponent(state));
            return {
                orgId: parsed.orgId ? BigInt(parsed.orgId) : undefined,
                tenantName: parsed.tenantName,
                redirectTo: parsed.redirectTo,
            };
        } catch {
            // If parsing fails, return empty object
            return {};
        }
    }, [state]);

    // Decode error description
    const decodedErrorDescription = useMemo(() => {
        if (!error_description) return null;
        try {
            return decodeURIComponent(error_description);
        } catch {
            return error_description;
        }
    }, [error_description]);

    // Determine if consent was successful
    const isSuccess = admin_consent === 'True' && !errorCode;

    // Get user-friendly error message
    const errorInfo = useMemo(() => {
        if (!errorCode) return null;

        const knownError = errorCodeMessages[errorCode];
        if (knownError) {
            return {
                title: knownError.title,
                description: decodedErrorDescription || knownError.description,
            };
        }

        return {
            title: 'Admin Consent Failed',
            description: decodedErrorDescription || 'An unexpected error occurred during the consent process.',
        };
    }, [errorCode, decodedErrorDescription]);

    useEffect(() => {
        // Simulate processing time for better UX
        const timer = setTimeout(() => {
            setIsProcessing(false);
        }, 1500);

        return () => clearTimeout(timer);
    }, []);

    const handleReturnToLogin = () => {
        navigate({ to: '/login' });
    };

    if (isProcessing) {
        return (
            <PageController route={Route}>
                <AuthLoadingScreen title="Processing admin consent" hint="Setting up permissions for your tenant" />
            </PageController>
        );
    }

    return (
        <PageController route={Route}>
            <div className="min-h-screen w-full bg-muted">
                <div className="flex min-h-screen items-center justify-center">
                    <div className="w-full max-w-lg px-4">
                        <Card>
                            <div className="text-center">
                                <div className="mb-6">
                                    <img
                                        src="/static/curium-logo.svg"
                                        alt="Curium Logo"
                                        className="mx-auto h-16 w-auto"
                                    />
                                </div>

                                {isSuccess ? (
                                    <>
                                        <div className="mb-6 flex justify-center">
                                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                                                <CheckCircle className="h-10 w-10 text-green-600" />
                                            </div>
                                        </div>
                                        <h2 className="mb-4 font-bold text-3xl text-foreground">
                                            Admin Consent Granted
                                        </h2>
                                        <p className="mb-2 text-lg text-muted-foreground">
                                            You have successfully granted admin consent for{' '}
                                            {stateData?.tenantName ? (
                                                <span className="font-semibold">{stateData.tenantName}</span>
                                            ) : (
                                                'your organization'
                                            )}
                                            .
                                        </p>
                                        <p className="mb-8 text-base text-muted-foreground">
                                            All users in your tenant can now access Curium using single sign-on (SSO).
                                        </p>

                                        <div className="mb-8 rounded-lg bg-blue-50 p-4">
                                            <h3 className="mb-2 font-semibold text-blue-900">Next Steps:</h3>
                                            <ul className="space-y-1 text-left text-blue-800 text-sm">
                                                <li>• Users can now sign in using their organizational credentials</li>
                                                <li>• No additional setup is required for individual users</li>
                                            </ul>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="mb-6 flex justify-center">
                                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
                                                <AlertCircle className="h-10 w-10 text-error" />
                                            </div>
                                        </div>
                                        <h2 className="mb-4 font-bold text-3xl text-foreground">
                                            {errorInfo?.title || 'Admin Consent Failed'}
                                        </h2>
                                        <p className="mb-2 text-lg text-muted-foreground">
                                            {errorInfo?.description ||
                                                'We were unable to process the admin consent for your organization.'}
                                        </p>
                                        {errorCode && (
                                            <div className="mb-6 rounded-lg bg-red-50 p-4">
                                                <p className="text-red-800 text-sm">
                                                    <span className="font-semibold">Error Code: </span>
                                                    <code className="rounded bg-red-100 px-1 py-0.5">{errorCode}</code>
                                                </p>
                                                {decodedErrorDescription &&
                                                    decodedErrorDescription !== errorInfo?.description && (
                                                        <p className="mt-2 text-red-700 text-sm">
                                                            <span className="font-semibold">Details: </span>
                                                            {decodedErrorDescription}
                                                        </p>
                                                    )}
                                            </div>
                                        )}

                                        <div className="mb-8 rounded-lg bg-yellow-50 p-4">
                                            <h3 className="mb-2 font-semibold text-yellow-900">Troubleshooting:</h3>
                                            <ul className="space-y-1 text-left text-sm text-yellow-800">
                                                {errorCode === 'access_denied' ? (
                                                    <>
                                                        <li>
                                                            • If you changed your mind, you can try granting consent
                                                            again
                                                        </li>
                                                        <li>• Ensure you understand the permissions being requested</li>
                                                        <li>• Contact your IT administrator if you have concerns</li>
                                                    </>
                                                ) : errorCode === 'unauthorized_client' ? (
                                                    <>
                                                        <li>
                                                            • Contact your IT administrator to authorize this
                                                            application
                                                        </li>
                                                        <li>
                                                            • Verify that Curium is approved for use in your
                                                            organization
                                                        </li>
                                                        <li>
                                                            • Check your organization's application consent policies
                                                        </li>
                                                    </>
                                                ) : errorCode === 'server_error' ||
                                                  errorCode === 'temporarily_unavailable' ? (
                                                    <>
                                                        <li>• Wait a few minutes and try again</li>
                                                        <li>• Check the identity provider service status</li>
                                                        <li>• Clear your browser cache and cookies</li>
                                                    </>
                                                ) : (
                                                    <>
                                                        <li>• Ensure you have admin privileges in your organization</li>
                                                        <li>
                                                            • Check that your organization allows third-party app
                                                            consent
                                                        </li>
                                                        <li>• Try clearing your browser cache and cookies</li>
                                                    </>
                                                )}
                                                <li>• Contact support if the issue persists</li>
                                            </ul>
                                        </div>

                                        <div className="flex flex-col space-y-3">
                                            <Button
                                                type="primary"
                                                size="large"
                                                block
                                                onClick={handleReturnToLogin}
                                                icon={<LogIn />}
                                            >
                                                Try Again
                                            </Button>
                                            <Button
                                                type="secondary"
                                                size="large"
                                                block
                                                onClick={() => navigate({ to: '/' })}
                                                icon={<Home />}
                                            >
                                                Go to Homepage
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Card>

                        <div className="mt-6 text-center">
                            <p className="text-sm text-subtle-foreground">
                                Need help?{' '}
                                <a
                                    href="mailto:support@getcurium.com"
                                    className="font-medium text-primary-600 hover:text-primary-700"
                                >
                                    Contact Support
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </PageController>
    );
}
